import React, { useEffect, useMemo, useState } from "react";

import { Provider } from "~/api/providers";
import ProviderDetailsModal from "~/shared/components/ProviderDetailsModal";
import { useNow } from "~/shared/hooks/useNow";
import { VIDEO_CALL_LOCATION } from "~/shared/utils/constants";
import { formatAppointmentTime, isSlotInPast } from "~/shared/utils/dates";
import { useSignUpStore } from "~/stores";
import { tw } from "~/utils";
import { ChevronOpenIcon } from "../icons/ChevronOpenIcon";

interface ProviderCardProps {
  provider: Provider;
  location?: string;
  onTimeSelect?: (time: string) => void;
  selectedTime?: string | null;
}

const INITIAL_SLOTS_SHOWN = 6;

// Get display name for location
const getLocationDisplayName = (location?: string): string => {
  if (!location || location === VIDEO_CALL_LOCATION) {
    return "telehealth";
  }
  // Extract city name from "In person at Buffalo" -> "Buffalo"
  if (location.startsWith("In person at ")) {
    return location.replace("In person at ", "");
  }
  return location;
};

const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  location,
  onTimeSelect,
  selectedTime,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { patient } = useSignUpStore();

  // The server withholds slots inside the minimum-notice buffer when it answers,
  // but a patient can sit on this screen for an hour; without a clock the list
  // loaded at 1:55 keeps offering the 2:00 slot. Everything below reads
  // bookableSlots, never provider.availableSlots — selection is by INDEX, so a
  // list that is filtered for display but not for lookup hands the parent the
  // wrong time.
  const now = useNow();
  const bookableSlots = useMemo(
    () => provider.availableSlots.filter((slot) => !isSlotInPast(slot, now)),
    [provider.availableSlots, now],
  );

  useEffect(() => {
    if (selectedTime) {
      const index = bookableSlots.findIndex((slot) => slot === selectedTime);
      setSelectedIndex(index >= 0 ? index : null);
      // Auto-expand if selected time is beyond initial view
      if (index >= INITIAL_SLOTS_SHOWN) {
        setIsExpanded(true);
      }
    } else {
      setSelectedIndex(null);
    }
  }, [selectedTime, bookableSlots]);

  const handleTimeSelect = (index: number) => {
    if (selectedIndex === index) {
      setSelectedIndex(null);
      onTimeSelect?.("");
    } else {
      setSelectedIndex(index);
      onTimeSelect?.(bookableSlots[index]);
    }
  };

  // Count matching specialties
  const userSelectedSpecialties = patient.conditions || [];
  const matchingSpecialties = provider.metadata.specialties.filter((s) =>
    userSelectedSpecialties.includes(s),
  );
  const matchCount = matchingSpecialties.length;

  const totalSlots = bookableSlots.length;
  const hasMoreSlots = totalSlots > INITIAL_SLOTS_SHOWN;
  const visibleSlots = isExpanded
    ? bookableSlots
    : bookableSlots.slice(0, INITIAL_SLOTS_SHOWN);
  const hiddenCount = totalSlots - INITIAL_SLOTS_SHOWN;

  return (
    <>
      <article
        className={tw(
          "flex w-full flex-col gap-3 rounded-lg border bg-white p-3",
          selectedIndex !== null
            ? "border-green-500 shadow-md"
            : "border-gray-200",
        )}
      >
        {/* Header row: avatar, name, learn more */}
        <header className="flex items-center gap-3">
          {/* Smaller avatar */}
          {provider.avatarUrl ? (
            <img
              src={provider.avatarUrl}
              alt={`${provider.fullName}`}
              className="size-10 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-500">
              <span className="font-display text-lg">
                {provider.fullName ? provider.fullName.charAt(0) : "P"}
              </span>
            </div>
          )}

          {/* Name and credentials */}
          <div className="min-w-0 flex-1">
            <h2 className="font-display truncate text-base text-black">
              {provider.fullName}
            </h2>
            <p className="truncate text-xs text-neutral-500">
              {provider.credentials}
            </p>
          </div>

          {/* Learn more button */}
          <button
            className="flex shrink-0 items-center gap-0.5 text-xs text-neutral-500"
            onClick={() => setIsModalOpen(true)}
          >
            <span>Details</span>
            <ChevronOpenIcon className="size-3.5" />
          </button>
        </header>

        {/* Specialty match indicator: list the matched specialties */}
        {matchCount > 0 && (
          <div className="flex items-start gap-1.5 text-xs text-green-700">
            <svg
              className="mt-0.5 size-3.5 shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              <span className="font-medium">Specialties:</span>{" "}
              {matchingSpecialties.join(", ")}
            </span>
          </div>
        )}

        {/* Time slots - expandable grid */}
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-gray-500">
            {totalSlots} {getLocationDisplayName(location)} time
            {totalSlots !== 1 ? "s" : ""} available
          </span>

          {/* Grid of time buttons */}
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {visibleSlots.map((slot, index) => {
              const isSelected = selectedIndex === index;
              return (
                <button
                  key={`${provider.id}-time-${index}`}
                  type="button"
                  onClick={() => handleTimeSelect(index)}
                  className={tw(
                    "rounded-lg border px-2 py-2 text-sm font-medium transition-all",
                    isSelected
                      ? "border-green-500 bg-green-500 text-white"
                      : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 hover:bg-gray-100",
                  )}
                >
                  {formatAppointmentTime(slot)}
                </button>
              );
            })}
          </div>

          {/* Show more / Show less button */}
          {hasMoreSlots && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center gap-1 py-1 text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? (
                <>
                  <span>Show less</span>
                  <svg
                    className="size-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </>
              ) : (
                <>
                  <span>
                    {hiddenCount} more time{hiddenCount !== 1 ? "s" : ""}
                  </span>
                  <svg
                    className="size-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </>
              )}
            </button>
          )}
        </div>
      </article>

      <ProviderDetailsModal
        provider={provider}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTimeSelect={onTimeSelect}
      />
    </>
  );
};

export default ProviderCard;
